import { CommonModule } from "@angular/common";
import { BreakpointObserver } from "@angular/cdk/layout";
import { Overlay } from "@angular/cdk/overlay";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Observable, Subscription, of } from "rxjs";
import { switchMap } from "rxjs/operators";

import { UserService } from "../../authentication/user.service";
import { PaymentsService } from "../../services/payments.service";
import {
  NovaCompanionLauncherStateService,
  NovaVoiceMeter,
} from "../../services/nova-companion-launcher-state.service";
import { NovaCompanionSettingsService } from "../../services/nova-companion-settings.service";
import {
  NovaCompanionChatComponent,
  novaCompanionChatDialogConfig,
} from "../nova-companion-chat/nova-companion-chat.component";

/**
 * The two ranges that keep the round launcher: `desktop` and `tablet-landscape-touch` in
 * `bp.$devices` (src/sass/_breakpoints.scss), written out as media queries because the SCSS map
 * cannot be read from TypeScript. These are exactly the ranges where the bottom nav is hidden
 * (`--bottom-nav-height-padding: 0`, src/sass/_common.scss), so everywhere ELSE - the bottom nav is
 * showing - is the mobile tab. Keep both in step with that map: 1050px is `lg`, 768px is `md`.
 */
const ROUND_LAUNCHER_QUERIES = [
  "(min-width: 1050px)",
  "(min-width: 768px) and (max-width: 1049.98px) and (orientation: landscape) and (hover: none) and (pointer: coarse)",
];

/**
 * Nova Companion's floating entry-point widget (FLIP-173) - ported from the confirmed
 * design handoff's `nova-companion/companion-launcher/`. Deliberately has NO data
 * dependency at all, per product decision confirmed with Lizzy ("the FAB doesn't need
 * access to data, it just performs a function") - unlike NovaInsightWidgetComponent, it
 * doesn't gate on a visibility/eligibility service, only on the user being logged in and,
 * as of FLIP-175, the settings-driven enable/disable toggle.
 * Renders the same whether or not the Insight Card feature (FLIP-169/170/171/172) is
 * enabled - the two features share no runtime state.
 *
 * Minimized state lives in NovaCompanionLauncherStateService rather than a local field: when
 * minimized, this component hides its own big FAB entirely rather than rendering a "mini" badge
 * in its own bottom-right corner - the way back into the chat is
 * NovaCompanionHeaderBadgeComponent, docked in the page hero header instead (see that component's
 * header comment for why).
 *
 * This circle is ALSO the minimised state of a running voice call. Minimising during voice used to
 * collapse the sheet to a 320px pill of its own (FLIP-234's `.nova-companion-chat__dock`); that
 * pill is gone and this widget took its job, swapping the mascot for a live wave and its own
 * gradient for the listening/speaking pair the in-sheet status bar uses. Same object shrunk,
 * rather than a second widget in a second place with a second visual language.
 *
 * Wherever the bottom nav is showing (`isMobileLayout`) the circle is replaced by a tab that sits
 * directly on top of the nav - "Ask Nova" at rest, "Voice Chat Active" while a call is docked. The
 * tab is the same element with the same wiring, restyled: it opens the sheet, and the sheet closes
 * from its own X. There is no minimise-to-header there, so the stored `minimized$` preference is
 * ignored on mobile rather than deleted - it still governs desktop.
 */
@Component({
  selector: "app-nova-companion-fab",
  templateUrl: "./nova-companion-fab.component.html",
  styleUrls: ["./nova-companion-fab.component.scss"],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
})
export class NovaCompanionFabComponent implements OnInit, OnDestroy {
  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private paymentsService: PaymentsService,
    private launcherState: NovaCompanionLauncherStateService,
    private companionSettings: NovaCompanionSettingsService,
    private overlay: Overlay,
    private cdr: ChangeDetectorRef,
    private breakpoints: BreakpointObserver
  ) {}

  /** The circle itself, so it can take focus from the sheet as that sheet collapses. */
  @ViewChild("fab") fabRef?: ElementRef<HTMLElement>;

  minimized$ = this.launcherState.minimized$;
  enabled$ = this.companionSettings.enabled$;

  /**
   * Whether a voice call is running with the sheet collapsed - i.e. whether this circle is
   * currently Nova's minimised voice state rather than her launcher.
   *
   * A plain field fed by a subscription rather than `voiceDocked$ | async`, because open() has to
   * branch on it too and a getter over the observable would need a second subscription to read.
   */
  voiceDocked = false;

  /**
   * The live call, or null. Drives the wave heights, the gradient and the status label.
   *
   * @see meterSubscription for why this is not an async pipe.
   */
  meter: NovaVoiceMeter | null = null;

  private meterSubscription?: Subscription;

  /**
   * Whether the bottom nav is showing, i.e. whether this renders as the tab above it rather than
   * the round launcher. Seeded synchronously with isMatched() because observe() defers its first
   * emission a tick, which would paint the round launcher for one frame on every phone load.
   */
  isMobileLayout = !this.breakpoints.isMatched(ROUND_LAUNCHER_QUERIES);

  private layoutSubscription?: Subscription;

  ngOnInit(): void {
    this.layoutSubscription = this.breakpoints
      .observe(ROUND_LAUNCHER_QUERIES)
      .subscribe((state) => (this.isMobileLayout = !state.matches));

    // The meter is only subscribed while docked, so an open sheet or a closed Nova costs nothing:
    // the wave is published throughout the call, but this widget is only drawing it once the sheet
    // is out of the way.
    const docked$: Observable<boolean> = this.launcherState.voiceDocked$;
    this.meterSubscription = docked$
      .pipe(
        switchMap((docked) => {
          const wasDocked = this.voiceDocked;
          this.voiceDocked = docked;
          // The sheet's own minimise button is inside a subtree that is display:none by the time
          // this fires, so focus would otherwise fall back to <body> and leave a keyboard user
          // hunting for the circle. Deferred because the circle is not in the DOM yet either -
          // this very emission is what renders it.
          if (docked && !wasDocked) {
            setTimeout(() => this.fabRef?.nativeElement.focus());
          }
          return docked ? this.launcherState.voiceMeter$ : of(null);
        })
      )
      .subscribe((meter) => {
        this.meter = meter;
        // detectChanges, not markForCheck (which is all `| async` would do). Wave frames are
        // published from OUTSIDE the Angular zone - see renderVoiceWaveFrame in the chat component
        // - so nothing schedules a change-detection pass and an async-piped view would simply
        // never repaint. This repaints THIS component's subtree only, 20 times a second at worst,
        // which is the same trick and the same budget the chat uses for its own copy of the wave.
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.meterSubscription?.unsubscribe();
    this.layoutSubscription?.unsubscribe();
  }

  /**
   * FLIP-216: also gated on onboarding being finished, not just on being logged in. A
   * registering user is logged in for the whole of onboarding (the session is activated at
   * payment-success, before the onboarding screens run), so `isUserLoggedIn()` alone let the
   * FAB float over every onboarding screen - covering content the user has to read and act on,
   * and offering a chat in which Nova comments on the engagement of somebody who has not used
   * the product yet.
   *
   * Gated on the onboarding flag rather than on a route allow/deny list: it is the same
   * authority OnboardingGuard and app.component's showNav already use, and it covers
   * /register, /stripe-payments, /payment-success and /onboarding without enumerating routes
   * that would drift as registration-adjacent screens are added. Pre-login routes stay covered
   * by isUserLoggedIn() above.
   *
   * FLIP-247: also requires a confirmed-active subscription, matching showNav's third
   * condition. An already-onboarded user whose subscription has lapsed satisfies both checks
   * above (onboarding finished long ago) and lands on /subscription-expired via
   * SubscriptionGuard, still logged in - without this, the FAB would float over that
   * explanatory screen too.
   */
  get showFab(): boolean {
    return (
      this.userService.isUserLoggedIn() &&
      this.userService.isOnboardingComplete() &&
      this.paymentsService.isConfirmedActive
    );
  }

  /**
   * What the circle announces and what its tooltip says.
   *
   * Reuses the chat's own voiceStatusLabel verbatim, published through the meter rather than
   * recomputed here: that getter keys "Nova is speaking" off audio having actually started
   * (spokenChunkIndex >= 0) rather than off the state name, and a second implementation of that
   * rule would drift from it.
   */
  get statusLabel(): string {
    if (this.voiceDocked && this.meter) return this.meter.label;
    // The tab's visible text is "Ask Nova"; an accessible name that contains it keeps voice-control
    // users ("click Ask Nova") working, which "Open Nova" would not.
    return this.isMobileLayout ? "Ask Nova" : "Open Nova";
  }

  /** The tab's visible text. The live status stays in the sr-only region so it is announced. */
  get tabLabel(): string {
    return this.voiceDocked ? "Voice Chat Active" : "Ask Nova";
  }

  /** Keeps the *ngFor from rebuilding every bar's element on every frame. */
  trackVoiceBar(index: number): number {
    return index;
  }

  /**
   * Circle tap. Expands a collapsed call, or opens the chat if there isn't one.
   *
   * Restoring goes through the launcher service rather than dialog.open(): while docked the dialog
   * is already open, just collapsed, so opening would stack a second Nova - and a second
   * microphone - over the running one. The call itself keeps running either way; ending it is the
   * in-sheet stop button's job, which is where it was before the sheet was minimised.
   */
  open(): void {
    // The template already hides the FAB during onboarding; this makes the gate hold for any
    // other caller too, so Companion cannot be opened mid-onboarding by a path that reaches
    // this method without going through the hidden element.
    if (!this.showFab) {
      return;
    }

    if (this.voiceDocked) {
      this.launcherState.requestRestore();
      return;
    }

    // FLIP-234: shared config rather than an inline object. Two of its settings decide whether a
    // minimised voice session survives, so they cannot be per-call-site any more.
    this.dialog.open(NovaCompanionChatComponent, novaCompanionChatDialogConfig(this.overlay));
  }

  /** Minimize button - collapses to the header-docked badge without opening chat. */
  onMinimize(event: Event): void {
    event.stopPropagation();
    this.launcherState.minimize();
  }
}
